import { inject, Injectable } from '@angular/core';
import { CONFIG_TOKEN } from '@pxl/models/common';
import { LoginService, VeterinarianService } from '@pxl/models/contracts';
import {
    PracticeProfileModel,
    VeterinarianFeatureResponseModel,
    VeterinarianListModel,
    VeterinarianModel,
    VeterinarianService as VeterinarianBackendService,
} from '@pxl/shared/generated';
import { List } from 'linq-collections';
import { firstValueFrom, Observable } from 'rxjs';

@Injectable()
export class DefaultVeterinarianService implements VeterinarianService {
    private readonly veterinarianBackendService = inject(VeterinarianBackendService);
    private readonly loginService = inject(LoginService);
    private readonly config = inject(CONFIG_TOKEN);

    /**
     * Returns only veterinarians, which are linked with the user.
     */
    public getVeterinarians(): Promise<VeterinarianModel[]> {
        return firstValueFrom(
            this.veterinarianBackendService.getLinkedVeterinarians(this.config.API_VERSION, this.loginService.getToken()),
        );
    }

    public getVeterinarianList(): Promise<VeterinarianListModel> {
        return firstValueFrom(this.veterinarianBackendService.getVeterinarianList(this.config.API_VERSION, this.loginService.getToken()));
    }

    public getVeterinariansById(veterinarianIds: string[]): Promise<VeterinarianModel[]> {
        return firstValueFrom(
            this.veterinarianBackendService.getVeterinarian(veterinarianIds, this.config.API_VERSION, this.loginService.getToken()),
        );
    }

    public async getVeterinarian(veterinarianId: string): Promise<VeterinarianModel> {
        const veterinarians = await this.getVeterinariansById([veterinarianId]);

        return veterinarians[0];
    }

    public getPracticeProfile(vetIds: string): Observable<PracticeProfileModel> {
        return this.veterinarianBackendService.getPracticeProfile(vetIds, this.config.API_VERSION, this.loginService.getToken());
    }

    public getVeterinarianAsAnonymous(
        externalVeterinarianIdentifier: string,
        externalBranchIdentifier: string | null = null,
    ): Promise<VeterinarianModel> {
        // for some reason, the client is generated to accept and process "undefined", but not "null".
        return firstValueFrom(
            this.veterinarianBackendService.getVeterinarianForAnonymous(
                this.config.API_VERSION,
                externalVeterinarianIdentifier,
                externalBranchIdentifier ?? undefined,
            ),
        );
    }

    public async getVeterinariansMap<T>(items: T[], getIdFunction: (item: T) => string): Promise<Map<string, VeterinarianModel>> {
        const veterinarianIds: string[] = items.map((item: T) => getIdFunction(item));
        const filteredVeterinarianIds: string[] = new List<string>(veterinarianIds)
            .groupBy((key) => key)
            .select((item) => item.value.first())
            .toArray();

        const veterinarians = await this.getVeterinariansById(filteredVeterinarianIds);

        const veterinariansMap = new Map<string, VeterinarianModel>();

        veterinarians.forEach((veterinarian) => veterinariansMap.set(veterinarian.id, veterinarian));

        return veterinariansMap;
    }

    public getVeterinarianFeatures(id: VeterinarianModel['id']): Observable<VeterinarianFeatureResponseModel> {
        return this.veterinarianBackendService.getVeterinarianFeatures(id, this.config.API_VERSION, this.loginService.getToken());
    }

    public getVeterinarianFeaturesAsGuest(id: VeterinarianModel['id']): Observable<VeterinarianFeatureResponseModel> {
        return this.veterinarianBackendService.getVeterinarianFeaturesAsGuest(id, this.config.API_VERSION);
    }
}
